// Smoothed curve of everyone's scores for a finished game's ItWord, with a
// marker at this game's score and a line at the day's average
// (itword/templates/itword/partials/score_distribution.html). The curve is
// pre-smoothed server-side (see itword.utils.smoothed_distribution); its
// height is only "roughly how many players scored around here", so the y
// axis is hidden and the chart takes no hover.

import { setupCharts } from './charts.js'

// The curve's height at `x`, interpolating between its whole-number points
// for an average that falls between them. The curve always spans `x`, as it
// covers every score.
const heightAt = (points, x) => {
    const below = points.findLast((point) => point.x <= x)
    const above = points.find((point) => point.x >= x)
    if (below === above) return below.y
    return below.y + ((above.y - below.y) * (x - below.x)) / (above.x - below.x)
}

// Draws the average as a faint dashed line labelled "Avg" along the x axis,
// then the game's score as a line from the x axis up to the curve, with a dot
// and the score labelled where they meet.
const markers = ({ score, average, points }, colour) => {
    const scoreHeight = heightAt(points, score)
    return {
        id: 'markers',
        afterDatasetsDraw(chart) {
            const { ctx, chartArea, scales } = chart
            const font = getComputedStyle(chart.canvas).fontFamily
            ctx.save()
            ctx.lineWidth = 2
            ctx.setLineDash([4, 4])

            if (average !== null) {
                const x = scales.x.getPixelForValue(average)
                const faint = colour('text', 0.5)
                ctx.strokeStyle = faint
                ctx.fillStyle = faint
                ctx.beginPath()
                ctx.moveTo(x, chartArea.bottom)
                ctx.lineTo(x, chartArea.top)
                ctx.stroke()

                ctx.font = `12px ${font}`
                ctx.textAlign = 'left'
                ctx.textBaseline = 'bottom'
                ctx.fillText('Avg', x + 4, chartArea.bottom - 4)
            }

            const x = scales.x.getPixelForValue(score)
            const y = scales.y.getPixelForValue(scoreHeight)
            const text = colour('text')
            ctx.strokeStyle = text
            ctx.fillStyle = text
            ctx.beginPath()
            ctx.moveTo(x, chartArea.bottom)
            ctx.lineTo(x, y)
            ctx.stroke()

            ctx.setLineDash([])
            ctx.strokeStyle = colour('background')
            ctx.beginPath()
            ctx.arc(x, y, 5, 0, 2 * Math.PI)
            ctx.fill()
            ctx.stroke()

            ctx.font = `bold 12px ${font}`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'bottom'
            ctx.fillText(String(score), x, y - 8)
            ctx.restore()
        },
    }
}

const buildScoreDistributionChart = ({
    Chart,
    canvas,
    data,
    colour,
    animation,
}) => {
    const { points } = data

    return new Chart(canvas, {
        type: 'line',
        data: {
            datasets: [
                {
                    data: points,
                    borderColor: colour('primary'),
                    backgroundColor: colour('primary', 0.2),
                    fill: 'origin',
                    borderWidth: 2,
                    pointRadius: 0,
                    tension: 0.4,
                },
            ],
        },
        options: {
            maintainAspectRatio: false,
            animation,
            // No hover: the curve's height isn't an exact count to show
            events: [],
            // Room above the curve's peak for the score label
            layout: { padding: { top: 24 } },
            scales: {
                x: {
                    type: 'linear',
                    min: points[0].x,
                    max: points[points.length - 1].x,
                    ticks: { color: colour('text'), precision: 0 },
                    grid: { display: false },
                    border: { display: false },
                },
                y: { display: false, beginAtZero: true },
            },
        },
        plugins: [markers(data, colour)],
    })
}

setupCharts('[data-score-distribution-chart]', buildScoreDistributionChart)
